import React, { useEffect, useRef } from 'react';

// Antigravity-style particle field (Home hero only), matched to frame-by-frame
// measurements of antigravity.google: mostly-stationary field where random
// individual particles "burst" — darting a short eased distance (slight upward
// bias), rotating slightly, then resting at the new spot. Blue-noise seeding
// keeps spacing even; burst destinations are rejected if they would clump.
const HeroParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const isMobile = window.innerWidth < 768;
    const COUNT = isMobile ? 68 : 180;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const mouse = { x: -9999, y: -9999 };
    let particles = [];
    let raf = null;
    let w = 0;
    let h = 0;
    let minSpacing = 0;

    const COLORS = ['17, 170, 182', '5, 46, 108'];
    const WIDTHS = [1.6, 1.9];
    const NB = 8;
    const A_MIN = 0.35;
    const A_RANGE = 0.5;

    const STYLES = COLORS.map((c) =>
      Array.from({ length: NB }, (_, b) => `rgba(${c}, ${(A_MIN + ((b + 0.5) / NB) * A_RANGE).toFixed(3)})`)
    );
    const dashGroups = COLORS.map(() => WIDTHS.map(() => Array.from({ length: NB }, () => [])));
    const dotGroups = COLORS.map(() => Array.from({ length: NB }, () => []));

    // Best-candidate (Mitchell) sampling ≈ Poisson-disc blue noise
    const blueNoisePoints = (n) => {
      const pts = [];
      for (let i = 0; i < n; i++) {
        let bx = Math.random() * w, by = Math.random() * h, bestD = -1;
        for (let k = 0; k < 14; k++) {
          const cx = Math.random() * w, cy = Math.random() * h;
          let minD = Infinity;
          for (const q of pts) {
            const dx = q[0] - cx, dy = q[1] - cy;
            const d = dx * dx + dy * dy;
            if (d < minD) minD = d;
          }
          if (pts.length === 0) minD = 0;
          if (minD > bestD) { bestD = minD; bx = cx; by = cy; }
        }
        pts.push([bx, by]);
      }
      return pts;
    };

    const spawn = () => {
      particles = [];
      minSpacing = Math.sqrt((w * h) / COUNT) * 0.45;
      const raw = blueNoisePoints(Math.ceil(COUNT * 1.2));
      const now = performance.now();
      for (const [x, y] of raw) {
        if (particles.length >= COUNT) break;
        const behindHeadline = x > w * 0.3 && x < w * 0.7 && y > h * 0.15 && y < h * 0.6;
        if (behindHeadline && Math.random() > 0.6) continue;
        const dash = Math.random() < 0.34; // majority tiny dots, ~1/3 short dashes
        particles.push({
          x, y,
          ox: 0, oy: 0, ovx: 0, ovy: 0,
          bursting: false,
          nextBurst: now + Math.random() * 1400,
          burstStart: 0, burstDur: 0,
          sx: 0, sy: 0, tx: 0, ty: 0, sAngle: 0, tAngle: 0,
          dash,
          len: 3 + Math.pow(Math.random(), 2) * 13,   // 3–16px, skewed small
          wi: Math.random() < 0.5 ? 0 : 1,
          radius: 0.5 + Math.random() * 1,            // dots ~1–3px across
          angle: Math.random() * Math.PI * 2,
          ci: Math.random() < 0.55 ? 0 : 1,
          baseAlpha: 0.45 + Math.random() * 0.25,
          alphaAmp: 0.06 + Math.random() * 0.08,
          breathPhase: Math.random() * Math.PI * 2,
          breathSpeed: 0.0004 + Math.random() * 0.0005
        });
      }
      canvas.dataset.count = particles.length;
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      spawn();
    };

    // Pick a burst destination: short eased dart, slight upward bias, must stay
    // in bounds and not land too close to any neighbour (keeps even spacing).
    const pickDestination = (p) => {
      for (let tries = 0; tries < 5; tries++) {
        const d = 3 + Math.pow(Math.random(), 2) * 32;      // few px up to ~35px
        const a = Math.random() * Math.PI * 2;
        const tx = p.x + Math.cos(a) * d;
        const ty = p.y + Math.sin(a) * d - d * 0.12;        // weak upward bias
        if (tx < 4 || tx > w - 4 || ty < 4 || ty > h - 4) continue;
        let ok = true;
        for (const q of particles) {
          if (q === p) continue;
          const dx = q.x - tx, dy = q.y - ty;
          if (dx * dx + dy * dy < minSpacing * minSpacing) { ok = false; break; }
        }
        if (ok) return [tx, ty];
      }
      return null;
    };

    const easeInOut = (k) => (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2);

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      ctx.lineCap = 'round';

      for (let ci = 0; ci < 2; ci++) {
        for (let b = 0; b < NB; b++) {
          dashGroups[ci][0][b].length = 0;
          dashGroups[ci][1][b].length = 0;
          dotGroups[ci][b].length = 0;
        }
      }

      for (const p of particles) {
        // Burst state machine: rest → eased dart → rest
        if (!p.bursting && t >= p.nextBurst) {
          const dest = pickDestination(p);
          if (dest) {
            p.bursting = true;
            p.burstStart = t;
            p.burstDur = 250 + Math.random() * 300;
            p.sx = p.x; p.sy = p.y;
            p.tx = dest[0]; p.ty = dest[1];
            p.sAngle = p.angle;
            p.tAngle = p.angle + (Math.random() - 0.5) * 1.2;
          } else {
            p.nextBurst = t + 300 + Math.random() * 500;
          }
        }
        if (p.bursting) {
          const k = (t - p.burstStart) / p.burstDur;
          if (k >= 1) {
            p.x = p.tx; p.y = p.ty; p.angle = p.tAngle;
            p.bursting = false;
            p.nextBurst = t + 400 + Math.random() * 900;
          } else {
            const e = easeInOut(k);
            p.x = p.sx + (p.tx - p.sx) * e;
            p.y = p.sy + (p.ty - p.sy) * e;
            p.angle = p.sAngle + (p.tAngle - p.sAngle) * e;
          }
        }

        // Secondary: soft cursor repulsion with spring ease-back
        if (!isMobile) {
          const dx = (p.x + p.ox) - mouse.x;
          const dy = (p.y + p.oy) - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 16900 && d2 > 0.01) {
            const dist = Math.sqrt(d2);
            const force = (1 - dist / 130) * 1.2;
            p.ovx += (dx / dist) * force;
            p.ovy += (dy / dist) * force;
          }
        }
        p.ovx += -p.ox * 0.02;
        p.ovy += -p.oy * 0.02;
        p.ovx *= 0.88;
        p.ovy *= 0.88;
        p.ox += p.ovx;
        p.oy += p.ovy;

        p.px = p.x + p.ox;
        p.py = p.y + p.oy;

        const alpha = p.baseAlpha + Math.sin(t * p.breathSpeed + p.breathPhase) * p.alphaAmp;
        let b = ((alpha - A_MIN) / A_RANGE * NB) | 0;
        if (b < 0) b = 0; else if (b >= NB) b = NB - 1;

        if (p.dash) dashGroups[p.ci][p.wi][b].push(p);
        else dotGroups[p.ci][b].push(p);
      }

      for (let ci = 0; ci < 2; ci++) {
        for (let wi = 0; wi < 2; wi++) {
          ctx.lineWidth = WIDTHS[wi];
          for (let b = 0; b < NB; b++) {
            const group = dashGroups[ci][wi][b];
            if (!group.length) continue;
            ctx.strokeStyle = STYLES[ci][b];
            ctx.beginPath();
            for (const p of group) {
              const half = p.len / 2;
              const hx = Math.cos(p.angle) * half;
              const hy = Math.sin(p.angle) * half;
              ctx.moveTo(p.px - hx, p.py - hy);
              ctx.lineTo(p.px + hx, p.py + hy);
            }
            ctx.stroke();
          }
        }
        for (let b = 0; b < NB; b++) {
          const group = dotGroups[ci][b];
          if (!group.length) continue;
          ctx.fillStyle = STYLES[ci][b];
          ctx.beginPath();
          for (const p of group) {
            ctx.moveTo(p.px + p.radius, p.py);
            ctx.arc(p.px, p.py, p.radius, 0, Math.PI * 2);
          }
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(draw);
    };

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && raf === null) {
        raf = requestAnimationFrame(draw);
      } else if (!entry.isIntersecting && raf !== null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    }, { threshold: 0 });

    resize();
    observer.observe(canvas);
    window.addEventListener('resize', resize);
    if (!isMobile) {
      window.addEventListener('mousemove', onMouse);
      window.addEventListener('mouseout', onLeave);
    }

    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('mouseout', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0 pointer-events-none"
      aria-hidden="true"
      data-testid="hero-particles-canvas"
    />
  );
};

export default HeroParticles;
